
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Search, Hand, Pencil, AlertCircle, X, ArrowRight, CheckCircle, FileClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import { SearchableSelect } from '@/components/SearchableSelect';
import { sortSections } from '@/lib/sectionSort';
import api from '@/lib/api';

const priorityColors = { High: 'bg-destructive/10 text-destructive border-destructive/20', Medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20', Low: 'bg-muted text-muted-foreground border-border' };

export default function TasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  // Persisted filters across page reloads. Cleared on logout via AuthContext.logout()
  // which removes the `nycd-tasks-filters` key.
  const PERSISTED_FILTERS_KEY = 'nycd-tasks-filters';
  const persisted = (() => {
    try { return JSON.parse(localStorage.getItem(PERSISTED_FILTERS_KEY) || 'null') || {}; }
    catch { return {}; }
  })();
  const [search, setSearch] = useState(persisted.search || '');
  const [filterMyTodos, setFilterMyTodos] = useState(!!persisted.filterMyTodos);
  const [filterProject, setFilterProject] = useState(persisted.filterProject || '');
  const [filterPriority, setFilterPriority] = useState(persisted.filterPriority || '');
  const [showCompleted, setShowCompleted] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectSections, setProjectSections] = useState([]);
  const [reminders, setReminders] = useState([]);

  const fetchTasks = () => api.get('/tasks').then(r => setTasks(r.data));
  const fetchReminders = () => api.get('/action-item-reminders').then(r => setReminders(r.data)).catch(() => {});
  useEffect(() => {
    fetchTasks();
    fetchReminders();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  // Honor ?mine=1 query param — clicking the Task Completion card on the
  // dashboard navigates here with the My To-Dos filter pre-applied. The
  // param is stripped from the URL after applying so a refresh keeps state.
  useEffect(() => {
    if (searchParams.get('mine') === '1') {
      setFilterMyTodos(true);
      const next = new URLSearchParams(searchParams);
      next.delete('mine');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Persist filters + search so they survive navigation/reloads (showCompleted
  // intentionally resets). AuthContext.logout() clears the storage key so the
  // next user starts clean.
  useEffect(() => {
    try {
      localStorage.setItem(PERSISTED_FILTERS_KEY, JSON.stringify({
        filterMyTodos, filterProject, filterPriority, search,
      }));
    } catch { /* quota/privacy mode - ignore */ }
  }, [filterMyTodos, filterProject, filterPriority, search]);

  // Load sections when project changes
  const loadSections = async (projectId) => {
    if (!projectId) { setProjectSections([]); return; }
    try {
      const r = await api.get(`/projects/${projectId}/sections`);
      setProjectSections(r.data);
    } catch { setProjectSections([]); }
  };

  const openCreate = () => {
    setEditTask(null);
    setForm({ title: '', description: '', project_id: '', contract_section_id: '', assigned_to: '', priority: 'Medium', due_date: '' });
    setProjectSections([]);
    setSheetOpen(true);
  };

  const openEdit = async (t) => {
    setEditTask(t);
    setForm({ ...t });
    if (t.project_id) await loadSections(t.project_id);
    else setProjectSections([]);
    setSheetOpen(true);
  };

  const handleProjectChange = async (projectId) => {
    setForm({ ...form, project_id: projectId, contract_section_id: '' });
    await loadSections(projectId);
  };

  const clearProject = () => {
    setForm({ ...form, project_id: '', contract_section_id: '' });
    setProjectSections([]);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error('Title required');
    if (form.project_id && !form.contract_section_id) {
      return toast.error('Please select a Section when linking to a Project');
    }
    try {
      const payload = { ...form };
      if (!payload.project_id) {
        payload.project_id = null;
        payload.contract_section_id = null;
      }
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, payload);
        toast.success('To-Do updated');
      } else {
        await api.post('/tasks', payload);
        toast.success('To-Do created');
      }
      setSheetOpen(false);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving To-Do'); }
  };

  const claimTask = async (tid) => {
    try {
      await api.post(`/tasks/${tid}/claim`);
      toast.success('To-Do claimed!');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error claiming'); }
  };

  const dismissReminder = async (reminderId) => {
    await api.put(`/action-item-reminders/${reminderId}/dismiss`);
    setReminders(prev => prev.filter(r => r.id !== reminderId));
  };

  const dismissAllReminders = async () => {
    if (reminders.length === 0) return;
    await api.put('/action-item-reminders/dismiss-all');
    setReminders([]);
    toast.success('All action item suggestions cleared');
  };

  const createTodoFromReminder = async (reminder) => {
    setEditTask(null);
    // Map the reminder's entity onto the new task form so the linkage is
    // preserved (project / lead / proposal / rfp). status_log_id is carried
    // through so the Status Updates view can show the linked To-Do back.
    const baseForm = {
      title: reminder.action_items.length > 80 ? reminder.action_items.substring(0, 80) + '...' : reminder.action_items,
      description: `From status update: ${reminder.action_items}`,
      project_id: '',
      lead_id: '',
      proposal_id: '',
      rfp_id: '',
      status_log_id: reminder.status_id || '',
      contract_section_id: '',
      assigned_to: '',
      priority: 'Medium',
      due_date: '',
    };
    const et = reminder.entity_type || 'project';
    if (et === 'project') {
      baseForm.project_id = reminder.entity_id || reminder.project_id || '';
      if (baseForm.project_id) await loadSections(baseForm.project_id);
      else setProjectSections([]);
    } else if (et === 'lead') {
      baseForm.lead_id = reminder.entity_id || '';
      setProjectSections([]);
    } else if (et === 'proposal') {
      baseForm.proposal_id = reminder.entity_id || '';
      setProjectSections([]);
    } else if (et === 'rfp') {
      baseForm.rfp_id = reminder.entity_id || '';
      setProjectSections([]);
    } else {
      setProjectSections([]);
    }
    setForm(baseForm);
    setSheetOpen(true);
  };

  const updateTaskStatus = async (tid, status) => {
    try {
      await api.put(`/tasks/${tid}`, { status });
      toast.success('Status updated');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating status'); }
  };

  // Apply filters
  let filtered = tasks.filter(t =>
    (t.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.project_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.lead_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.proposal_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.pipeline_label || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.section_name || '').toLowerCase().includes(search.toLowerCase())
  );
  if (filterMyTodos && user) {
    filtered = filtered.filter(t => t.assigned_to === user.id);
  }
  if (filterProject) {
    filtered = filtered.filter(t => t.project_id === filterProject);
  }
  if (filterPriority) {
    filtered = filtered.filter(t => t.priority === filterPriority);
  }

  // Split completed vs active
  if (showCompleted) {
    filtered = filtered.filter(t => t.status === 'Completed');
  } else {
    filtered = filtered.filter(t => t.status !== 'Completed');
  }

  // Sort: unclaimed first, then by due date (nearest first, no due date at bottom)
  filtered.sort((a, b) => {
    const aUnclaimed = !a.assigned_to ? 0 : 1;
    const bUnclaimed = !b.assigned_to ? 0 : 1;
    if (aUnclaimed !== bUnclaimed) return aUnclaimed - bUnclaimed;
    const aDate = a.due_date || '';
    const bDate = b.due_date || '';
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return aDate.localeCompare(bDate);
  });

  const hasActiveFilters = filterMyTodos || filterProject || filterPriority || showCompleted || search;

  const clearAllFilters = () => {
    setFilterMyTodos(false);
    setFilterProject('');
    setFilterPriority('');
    setShowCompleted(false);
    setSearch('');
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">To-Dos<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{filtered.length} {showCompleted ? 'completed' : 'active'}</span></h1>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid="create-task-button">
          <Plus className="h-4 w-4 mr-1" /> New To-Do
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search to-dos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="tasks-search" />
        </div>

        <Button
          variant={filterMyTodos ? 'default' : 'outline'}
          size="sm"
          className={`text-xs h-9 gap-1.5 ${filterMyTodos ? 'bg-primary text-primary-foreground' : 'border-border'}`}
          onClick={() => setFilterMyTodos(!filterMyTodos)}
          data-testid="filter-my-todos"
        >
          <Hand className="h-3 w-3" /> My To-Dos
        </Button>

        <SearchableSelect
          value={filterProject || ''}
          onValueChange={v => setFilterProject(v)}
          options={[{ value: '', label: 'All Projects' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
          placeholder="All Projects"
          searchPlaceholder="Search projects..."
          className="w-48 h-9 text-xs"
          data-testid="filter-project"
        />

        <Select value={filterPriority} onValueChange={v => setFilterPriority(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 bg-card border-border text-xs" data-testid="filter-priority">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Priorities</SelectItem>
            <SelectItem value="High" className="text-xs">High</SelectItem>
            <SelectItem value="Medium" className="text-xs">Medium</SelectItem>
            <SelectItem value="Low" className="text-xs">Low</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={showCompleted ? 'default' : 'outline'}
          size="sm"
          className={`text-xs h-9 gap-1.5 ${showCompleted ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'border-border'}`}
          onClick={() => setShowCompleted(!showCompleted)}
          data-testid="filter-completed"
        >
          <CheckCircle className="h-3 w-3" /> Completed
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="text-xs h-9 text-muted-foreground hover:text-foreground gap-1" onClick={clearAllFilters} data-testid="clear-all-filters">
            <X className="h-3 w-3" /> Clear Filters
          </Button>
        )}
      </div>

      {/* Action Item Reminders */}
      {reminders.length > 0 && (
        <div className="space-y-2" data-testid="action-item-reminders">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Action Item Suggestions <span className="font-mono tabular-nums">({reminders.length})</span></p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-sm text-muted-foreground hover:text-foreground border-white/15 px-2.5"
              onClick={dismissAllReminders}
              data-testid="dismiss-all-reminders-btn"
            >
              Clear All
            </Button>
          </div>
          {reminders.map(r => {
            const et = r.entity_type || 'project';
            const navTarget = et === 'project'
              ? `/projects/${r.entity_id || r.project_id}?tab=status`
              : et === 'lead' ? `/leads?lead=${r.entity_id}`
              : et === 'proposal' ? `/proposals?proposal=${r.entity_id}`
              : et === 'rfp' ? `/leads?tab=rfp&rfp=${r.entity_id}`
              : '#';
            const label = r.entity_label || r.project_name || '';
            return (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-md border border-amber-500/20 bg-amber-500/5" data-testid={`reminder-${r.id}`}>
                <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  {/* Entity label clickable → opens the source record's status log */}
                  <button
                    type="button"
                    onClick={() => navigate(navTarget)}
                    className="text-xs text-amber-400 font-medium hover:text-amber-300 hover:underline inline-flex items-center gap-1"
                    data-testid={`reminder-open-status-${r.id}`}
                  >
                    {label}
                    <FileClock className="h-3 w-3" />
                  </button>
                  <p className="text-sm mt-0.5">{r.action_items}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {r.created_by_name && `By ${r.created_by_name} · `}{new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-amber-400 hover:text-amber-300 gap-1"
                    onClick={() => navigate(navTarget)}
                    data-testid={`reminder-status-log-${r.id}`}
                  >
                    <FileClock className="h-3 w-3" /> Status Log
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-primary hover:text-primary/80 gap-1" onClick={() => createTodoFromReminder(r)} data-testid={`reminder-create-${r.id}`}>
                    <ArrowRight className="h-3 w-3" /> Create To-Do
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={() => dismissReminder(r.id)} data-testid={`reminder-dismiss-${r.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border border-border rounded-md bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">To-Do</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Project</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Section</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Assignee</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Priority</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Due</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(t => (
              <TableRow key={t.id} className="border-border hover:bg-secondary/30" data-testid={`task-row-${t.id}`}>
                <TableCell>
                  <div><p className="text-sm font-medium">{t.title}</p>{t.description && <div className="mt-0.5"><RichTextDisplay value={t.description} collapsedHeight={20} /></div>}</div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{t.project_name || t.lead_name || t.proposal_name || t.pipeline_label || <span className="text-muted-foreground/50">General</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{t.section_display || '-'}</TableCell>
                <TableCell className="text-sm">{t.assignee_name || <span className="text-amber-400 text-xs">Unassigned</span>}</TableCell>
                <TableCell><Badge className={`text-xs ${priorityColors[t.priority] || ''}`}>{t.priority}</Badge></TableCell>
                <TableCell>
                  <Select value={t.status} onValueChange={v => updateTaskStatus(t.id, v)}>
                    <SelectTrigger className="w-28 h-7 bg-transparent border-0 text-xs p-0"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {['Not Started', 'In Progress', 'Completed', 'On Hold'].map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{t.due_date || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {!t.assigned_to && (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-400 border-emerald-500/20" onClick={() => claimTask(t.id)} data-testid={`claim-task-${t.id}`}>
                        <Hand className="h-3 w-3 mr-1" /> Claim
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No to-dos found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">{editTask ? 'Edit To-Do' : 'New To-Do'}</SheetTitle>
            <SheetDescription>
              {form.project_id ? 'Linked to a project. Section required'
               : form.lead_id ? 'Linked to a Lead'
               : form.proposal_id ? 'Linked to a Proposal'
               : form.rfp_id ? 'Linked to an RFP'
               : 'General to-do. No record link'}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Title *</Label><Input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-background border-border" data-testid="task-form-title" /></div>
            <div><Label className="text-xs text-muted-foreground">Description</Label><RichTextEditor value={form.description || ''} onChange={v => setForm({ ...form, description: v })} placeholder="Task description..." /></div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Project</Label>
                {form.project_id && <Button variant="ghost" size="sm" className="text-xs h-5 text-muted-foreground" onClick={clearProject}>Clear</Button>}
              </div>
              <SearchableSelect
                value={form.project_id || ''}
                onValueChange={handleProjectChange}
                options={projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))}
                placeholder="None (General To-Do)"
                searchPlaceholder="Search projects..."
                data-testid="task-form-project"
              />
            </div>

            {/* Section dropdown - only shown when project selected */}
            {form.project_id && (
              <div>
                <Label className="text-xs text-muted-foreground">Section *</Label>
                <Select value={form.contract_section_id || ''} onValueChange={v => setForm({ ...form, contract_section_id: v })}>
                  <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select section" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {sortSections(projectSections).map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.section_number ? `${s.section_number} - ` : ''}{s.section_name}{s.source_tag && s.source_tag !== 'Original Contract' ? ` [${s.source_tag}]` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {projectSections.length === 0 && <p className="text-xs text-amber-400 mt-1">No sections found for this project</p>}
              </div>
            )}

            <div>
              <Label className="text-xs text-muted-foreground">Assign To</Label>
              <Select value={form.assigned_to || ''} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Priority</Label>
                <Select value={form.priority || 'Medium'} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">{['High', 'Medium', 'Low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-background border-border" /></div>
            </div>
            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="task-form-submit">{editTask ? 'Update To-Do' : 'Create To-Do'}</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
